// src/pages/dashboards/components/AddWidgetModal.tsx
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  Spinner,
} from '@/components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useMetricas, useAddWidget } from '@/hooks/queries/useReportes';
import { TipoWidget } from '@/types/reporte';
import type { Metrica } from '@/types/reporte';

const TIPO_WIDGET_LABELS: Record<TipoWidget, string> = {
  [TipoWidget.CONTADOR]: 'Contador',
  [TipoWidget.GRAFICO_LINEA]: 'Grafico de Linea',
  [TipoWidget.GRAFICO_BARRA]: 'Grafico de Barras',
  [TipoWidget.GRAFICO_PIE]: 'Grafico Pie',
  [TipoWidget.GRAFICO_DONA]: 'Grafico Dona',
  [TipoWidget.TABLA]: 'Tabla',
  [TipoWidget.LISTA]: 'Lista',
  [TipoWidget.PROGRESO]: 'Barra de Progreso',
  [TipoWidget.TENDENCIA]: 'Tendencia',
};

const schema = z.object({
  metrica_id: z.string().min(1, 'Selecciona una metrica'),
  titulo_personalizado: z.string().max(100, 'Maximo 100 caracteres').optional(),
  tipo_widget: z.nativeEnum(TipoWidget).optional(),
});

type FormData = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onClose: () => void;
  dashboardId: string;
}

export function AddWidgetModal({ open, onClose, dashboardId }: Props) {
  const { data: metricas, isLoading: loadingMetricas } = useMetricas();
  const addWidgetMutation = useAddWidget();
  const [selectedMetrica, setSelectedMetrica] = useState<Metrica | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      metrica_id: '',
      titulo_personalizado: '',
      tipo_widget: undefined,
    },
  });

  const metricaId = watch('metrica_id');
  const tipoWidget = watch('tipo_widget');

  // Actualizar metrica seleccionada y tipo widget por defecto
  useEffect(() => {
    if (metricaId && metricas) {
      const metrica = metricas.find((m) => m.id === metricaId);
      setSelectedMetrica(metrica ?? null);
      if (metrica && !tipoWidget) {
        setValue('tipo_widget', metrica.tipo_widget_default);
      }
    } else {
      setSelectedMetrica(null);
    }
  }, [metricaId, metricas, setValue, tipoWidget]);

  // Reset form cuando se abre/cierra
  useEffect(() => {
    if (open) {
      reset({
        metrica_id: '',
        titulo_personalizado: '',
        tipo_widget: undefined,
      });
      setSelectedMetrica(null);
    }
  }, [open, reset]);

  const onSubmit = async (data: FormData) => {
    try {
      await addWidgetMutation.mutateAsync({
        dashboardId,
        data: {
          metrica_id: data.metrica_id,
          titulo_personalizado: data.titulo_personalizado || undefined,
          tipo_widget: data.tipo_widget,
        },
      });
      onClose();
    } catch {
      // Error handling is done by react-query
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Agregar Widget</DialogTitle>
        </DialogHeader>

        {loadingMetricas ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label htmlFor="metrica_id">Metrica *</Label>
              <Select
                value={metricaId}
                onValueChange={(value) => {
                  setValue('metrica_id', value);
                  // Reset tipo_widget para usar el default de la nueva metrica
                  setValue('tipo_widget', undefined);
                }}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar metrica" />
                </SelectTrigger>
                <SelectContent>
                  {metricas?.map((metrica) => (
                    <SelectItem key={metrica.id} value={metrica.id}>
                      {metrica.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.metrica_id && (
                <p className="text-sm text-red-500 mt-1">{errors.metrica_id.message}</p>
              )}
              {selectedMetrica?.descripcion && (
                <p className="text-xs text-gray-500 mt-1">{selectedMetrica.descripcion}</p>
              )}
            </div>

            <div>
              <Label htmlFor="titulo_personalizado">Titulo personalizado</Label>
              <Input
                id="titulo_personalizado"
                {...register('titulo_personalizado')}
                placeholder={selectedMetrica?.nombre ?? 'Opcional'}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Deja vacio para usar el nombre de la metrica
              </p>
              {errors.titulo_personalizado && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.titulo_personalizado.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="tipo_widget">Tipo de widget</Label>
              <Select
                value={tipoWidget ?? selectedMetrica?.tipo_widget_default ?? ''}
                onValueChange={(value) => setValue('tipo_widget', value as TipoWidget)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TIPO_WIDGET_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                      {selectedMetrica?.tipo_widget_default === value && ' (recomendado)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting || !metricaId}>
                {isSubmitting ? 'Agregando...' : 'Agregar widget'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
